// src/pages/CmsAdmins.jsx  (이름 바꾸고 라우팅도 이 컴포넌트로 연결)
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../../../utils/cmsAxios";
import SortableTable from "../../CommonComponents/SortableTable";
import PeriodPicker, { toKstIsoRange } from "../../CommonComponents/PeriodPicker";
import { FiPlus } from "react-icons/fi";
import { Calendar } from "lucide-react";

const APPLIED_DEFAULT = {
  searchField: "all",
  keyword: "",
  useStatus: "all",     // all | active | inactive
  roleType: "all",      // all | SUPER | MANAGER | STAFF
  loginStart: "",
  loginEnd: "",
};

const CmsAdmins = () => {
  const navigate = useNavigate();

  // 🔎 검색/필터 상태
  const [searchField, setSearchField] = useState("all");     // all | username | email
  const [keyword, setKeyword] = useState("");
  const [useStatus, setUseStatus] = useState("all");         // all | active | inactive
  const [roleType, setRoleType] = useState("all");           // all | SUPER | MANAGER | STAFF

  const [admins, setAdmins] = useState([]);

  // 날짜 선택(최종 로그인일)
  const [showLoginPicker, setShowLoginPicker] = useState(false);
  const [loginMode, setLoginMode] = useState("single");      // 'single' | 'range'
  const [loginSingle, setLoginSingle] = useState(null);
  const [loginRange, setLoginRange] = useState({ from: null, to: null });

  const [loading, setLoading] = useState(false);
  const [, setOpenAlert] = useState(false);
  const [, setAlertMsg] = useState("");
  const [, setOpenDelete] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const PAGE_SIZE = 10;
  const BLOCK_SIZE = 10;


  const withDateTooltip = useCallback((v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    const tip = `${y}.${m}.${dd} ${hh}:${mm}:${ss}`;
    return <time dateTime={d.toISOString()} title={tip}>{`${y}-${m}-${dd}`}</time>;
  }, []);

  const getCurrentLoginParams = useCallback(() => {
    if (loginMode === "single" && loginSingle) {
      return toKstIsoRange(loginSingle, loginSingle, "login_start", "login_end");
    }
    if (loginMode === "range" && loginRange.from) {
      return toKstIsoRange(loginRange.from, loginRange.to || loginRange.from, "login_start", "login_end");
    }
    return {};
  }, [loginMode, loginSingle, loginRange]);

  const [applied, setApplied] = useState(APPLIED_DEFAULT);

  // 목록 가져오기 (필요시 서버쪽 필터 파라미터 연결)
  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_FASTAPI_ADS_URL}/admin/list`;
      // 서버 측 필터가 준비되면 아래 params에 applied 사용
      const { data } = await axios.get(url/*, { params: applied }*/);
      setAdmins(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("관리자 목록을 불러오지 못했습니다", e);
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  // 프론트 필터링 (서버 필터 준비 전 임시)
  const filtered = useMemo(() => {
    const q = (applied.keyword || "").trim().toLowerCase();
    const hasRange = Boolean(applied.loginStart && applied.loginEnd);
    const startMs = hasRange ? new Date(applied.loginStart).getTime() : null;
    const endMs = hasRange ? new Date(applied.loginEnd).getTime() : null;

    return (admins || []).filter((a) => {
      // 사용여부
      if (applied.useStatus !== "all") {
        const active = Number(a.is_active) === 1;
        if (applied.useStatus === "active" && !active) return false;
        if (applied.useStatus === "inactive" && active) return false;
      }
      // 역할
      if (applied.roleType !== "all") {
        if (String(a.role) !== applied.roleType) return false;
      }
      // 최종 로그인 기간
      if (hasRange) {
        const t = new Date(a.last_login_at || 0).getTime();
        if (!(t >= startMs && t <= endMs)) return false;
      }
      // 검색
      if (!q || applied.searchField === "all") return true;
      if (applied.searchField === "username") return String(a.username || "").toLowerCase().includes(q);
      if (applied.searchField === "email") return String(a.email || "").toLowerCase().includes(q);
      return true;
    });
  }, [admins, applied]);


  const isDirty = useMemo(() => {
    const cur = getCurrentLoginParams();
    const curStart = cur.login_start || "";
    const curEnd = cur.login_end || "";
    return !(
      searchField === applied.searchField &&
      (keyword || "") === (applied.keyword || "") &&
      useStatus === applied.useStatus &&
      roleType === applied.roleType &&
      (curStart || "") === (applied.loginStart || "") &&
      (curEnd || "") === (applied.loginEnd || "")
    );
  }, [searchField, keyword, useStatus, roleType, applied, getCurrentLoginParams]);

  // 페이징
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const [pageState, setPage] = useState(1);
  const blockIndex = Math.floor((pageState - 1) / BLOCK_SIZE);
  const blockStart = blockIndex * BLOCK_SIZE + 1;
  const blockEnd = Math.min(blockStart + BLOCK_SIZE - 1, totalPages);
  // const showPager = total > PAGE_SIZE;

  const paged = useMemo(() => {
    const start = (pageState - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filtered.slice(start, end);
  }, [filtered, pageState]);

  useEffect(() => { setPage(1); }, [applied]);
  useEffect(() => { if (pageState > totalPages) setPage(totalPages); }, [pageState, totalPages]);

  const handleCreateClick = () => {
    // navigate("/admin/list/create", { state: { type: "admin" } });
    navigate("/admin/list/create");
  };

  const isSelectable = useCallback((row) => Number(row?.is_active) !== 1, []);

  const selectableTotal = useMemo(
    () => paged.filter(isSelectable).length,
    [paged, isSelectable]
  );

  const allChecked = useMemo(
    () => selectableTotal > 0 && selectedIds.size === selectableTotal,
    [selectableTotal, selectedIds]
  );
  
  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const shouldUncheck = prev.size === selectableTotal;
      if (shouldUncheck) return new Set();
      return new Set(paged.filter(isSelectable).map(r => r.id));
    });
  }, [paged, selectableTotal, isSelectable]);

  const toggleOne = useCallback((id) => {
    const row = paged.find(r => r.id === id);
    if (!row || !isSelectable(row)) return; // Y면 무시
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, [paged, isSelectable]);

  const handleDelete = useCallback(async (id) => {
    const ids = id ? [id] : Array.from(selectedIds);
    if (ids.length === 0) return;

    try {
      await Promise.all(
        ids.map((id) =>
          axios.delete(`${process.env.REACT_APP_FASTAPI_ADS_URL}/admin/delete/${id}`)
        )
      );
      const idSet = new Set(ids);
      setAdmins((prev) => prev.filter((a) => !idSet.has(a.id)));
      setSelectedIds(new Set());
      setAlertMsg("삭제 되었습니다.");
      setOpenAlert(true);
    } catch (e) {
      console.error("관리자 ID 삭제 실패:", e);
      setAlertMsg("삭제 중 오류가 발생했습니다.");
      setOpenAlert(true);
    } finally {
      setOpenDelete(false); // 확인 모달 닫기
    }
  }, [selectedIds]);


  // 컬럼
  const columns = useMemo(() => ([
    {
      key: "_select", sortable: false, align: "center", headerClassName: "px-0 text-center", cellClassName: "px-0 text-center",
      header: (
        <input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="전체 선택" />
      ),
      render: (_v, row) => {
        const disabled = !isSelectable(row);
        const checked = selectedIds.has(row.id);
        return (
          <input
            type="checkbox" disabled={disabled} checked={checked}
            onChange={(e) => { e.stopPropagation(); toggleOne(row.id); }}
            aria-label={`select-${row.id}`}
          />
        );
      },
    },
    {
      key: "is_active", header: "사용여부", type: "string", sortable: true,
      render: (v) => (
        <span className={Number(v) === 1 ? "text-emerald-600" : "text-rose-600"}>
          {Number(v) === 1 ? "Y" : "N"}
        </span>
      ),
      sortBy: (row) => Number(row.is_active), align: "center",
    },
    { key: "role", header: "관리자 유형", type: "string", sortable: true },
    { key: "username", header: "관리자 ID", type: "string", sortable: true, render: (v) => <span className="line-clamp-1">{v}</span> },
    // { key: "name", header: "관리자 이름", type: "string", sortable: true, render: (v) => <span className="line-clamp-1">{v}</span> },
    { key: "email", header: "이메일", type: "string", sortable: true, render: (v) => <span className="line-clamp-1">{v || "-"}</span> },
    { key: "phone", header: "휴대폰 번호", type: "string", sortable: true, render: (v) => <span className="line-clamp-1">{v || "-"}</span> },
    { key: "created_at", header: "가입일", type: "date", sortable: true, render: withDateTooltip },
    { key: "last_login_at", header: "최종 로그인", type: "date", sortable: true, render: withDateTooltip },
  ]), [withDateTooltip, allChecked, toggleAll, toggleOne, selectedIds, isSelectable]);

  
  // 🔎 검색 / 초기화
  const handleSearch = () => {
    const cur = getCurrentLoginParams();
    setApplied({
      searchField,
      keyword,
      useStatus,
      roleType,
      loginStart: cur.login_start || "",
      loginEnd: cur.login_end || "",
    });
  };

  const handleReset = () => {
    setSearchField("all");
    setKeyword("");
    setUseStatus("all");
    setRoleType("all");
    setLoginMode("single");
    setLoginSingle(null);
    setLoginRange({ from: null, to: null });
    setApplied(APPLIED_DEFAULT);
    fetchAdmins();
  };


  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-[1440px] bg-white rounded-2xl shadow overflow-hidden">
        <div className="flex">
          <div className="flex-1 min-w-0 bg-neutral-50/70">
            <main className="pt-12 flex flex-col p-4 overflow-x-hidden w-full">
              <section className="px-4 py-2 w-full overflow-x-hidden">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xl font-semibold mb-4">관리자 목록</h2>
                </div>

                {/* 필터 바 */}
                <div className="rounded-lg bg-white px-3 py-3">
                  <div className="flex flex-wrap items-end gap-4">
                    {/* 검색 */}
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-neutral-500">검색</label>
                      <div className="flex items-center gap-2">
                        <select
                          value={searchField}
                          onChange={(e) => setSearchField(e.target.value)}
                          className="h-9 w-28 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                        >
                          <option value="all">전체</option>
                          <option value="username">관리자 ID</option>
                          <option value="email">이메일</option>
                        </select>
                        <input
                          value={keyword}
                          onChange={(e) => setKeyword(e.target.value)}
                          type="text"
                          placeholder={searchField === "username" ? "관리자 ID 입력" :
                            searchField === "email" ? "이메일 입력" : "검색어 입력"}
                          className="h-9 w-48 rounded-md border border-neutral-300 bg-white px-3 text-sm"
                        />
                      </div>
                    </div>

                    {/* 사용 여부 */}
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-neutral-500">사용 여부</label>
                      <select
                        value={useStatus}
                        onChange={(e) => setUseStatus(e.target.value)}
                        className="h-9 w-28 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                      >
                        <option value="all">전체</option>
                        <option value="active">사용</option>
                        <option value="inactive">중지</option>
                      </select>
                    </div>

                    {/* 관리자 유형 */}
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-neutral-500">관리자 유형</label>
                      <select
                        value={roleType}
                        onChange={(e) => setRoleType(e.target.value)}
                        className="h-9 w-32 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                      >
                        <option value="all">전체</option>
                        <option value="SUPER">최고 관리자</option>
                        <option value="MANAGER">중간 관리자</option>
                        <option value="STAFF">일반 관리자</option>
                      </select>
                    </div>

                    {/* 최종 로그인일 */}
                    <div className="flex flex-col gap-1 relative">
                      <label className="text-sm text-neutral-500">최종 로그인일</label>
                      <button
                        type="button"
                        onClick={() => setShowLoginPicker(true)}
                        className="h-9 w-[230px] inline-flex items-center justify-between gap-2
                                   rounded-md border border-neutral-300 bg-white px-2 text-sm whitespace-nowrap"
                      >
                        <span className="truncate">
                          {(() => {
                            const cur = getCurrentLoginParams();
                            if (cur.login_start && cur.login_end) {
                              const s = new Date(cur.login_start);
                              const e = new Date(cur.login_end);
                              const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                              return `${fmt(s)} ~ ${fmt(e)}`;
                            }
                            return "-";
                          })()}
                        </span>
                        <Calendar />
                      </button>

                      {showLoginPicker && (
                        <>
                          <div className="fixed inset-0 bg-black/30 z-[40]" onClick={() => setShowLoginPicker(false)} />
                          <div className="absolute z-[50] mt-2 rounded-lg border bg-white shadow-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <select
                                value={loginMode}
                                onChange={(e) => setLoginMode(e.target.value)}
                                className="h-8 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                              >
                                <option value="single">하루</option>
                                <option value="range">기간</option>
                              </select>
                            </div>
                            <PeriodPicker
                              mode={loginMode}
                              value={loginMode === "single" ? loginSingle : loginRange}
                              onChange={(val) => {
                                if (loginMode === "single") setLoginSingle(val || null);
                                else setLoginRange(val ?? { from: null, to: null });
                              }}
                              onConfirm={() => setShowLoginPicker(false)}
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {/* 액션 버튼 - 중앙 정렬 */}
                    <div className="basis-full flex justify-center gap-2 py-2">
                      <button
                        type="button"
                        onClick={handleReset}
                        className="h-9 px-6 rounded-md border border-neutral-300 text-white bg-neutral-500 hover:bg-neutral-400"
                      >
                        초기화
                      </button>
                      <button
                        type="button"
                        onClick={handleSearch}
                        disabled={!isDirty}
                        className={`h-9 px-9 rounded-md text-white shadow-sm ${isDirty ? "bg-[#7864F9] hover:bg-violet-600" : "bg-violet-400/40 cursor-not-allowed"}`}
                      >
                        검색
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 py-3">
                  <div className="text-sm text-neutral-600">
                    총 <span className="text-violet-500">{filtered.length} 건</span>
                  </div>
                  <div className="ml-auto flex gap-2">
                      <button
                        type="button"
                        onClick={handleCreateClick}
                        className="rounded-lg text-sm px-3 py-1.5 bg-[#7864F9]/10 text-[#7864F9] inline-flex items-center"
                      >
                        <FiPlus className="mr-1"/>등록
                      </button>
                      <span aria-hidden="true" className="mx-2 h-7 w-[2px] mt-[5px] bg-[#D9D9D9]"/>
                      <button
                        type="button"
                        onClick={handleDelete}
                        className="rounded-lg border px-3 py-1.5 text-sm bg-black/10 text-black/50"
                      >
                        삭제
                      </button>
                  </div>
                </div>

                <div className="rounded-xl border bg-white text-sm overflow-hidden flex flex-col h-[55vh] p-4">
                  {loading ? (
                    <div className="p-10 text-center text-gray-500">불러오는 중…</div>
                  ) : paged.length === 0 ? (
                    <div className="p-10 text-center text-gray-500">관리자가 없습니다.</div>
                  ) : (
                    <SortableTable
                      data={paged}
                      columns={columns}
                      defaultSort={{ key: "created_at", dir: "desc" }}
                      rowKey={(row) => `${row.id}`}
                      onRowClick={(row) => navigate(`/admin/${row.id}`)}
                      headerCellClassName="bg-neutral-50 text-black/50 first:rounded-tl-xl last:rounded-tr-xl text-sm"
                      bodyClassName="text-sm text-black/50"
                      headerPaddingClass="px-2 py-2"
                      cellPaddingClass="px-2 py-2"
                      columnWidths={["30px", "90px", "110px", "100px", "150px", "150px", "110px", "110px"]}
                    />
                  )}
                </div>

                {/* 페이징 */}
                {totalPages > 1 && (
                  <div className="flex px-3 py-3 border-t">
                    <div className="flex justify-center items-center gap-1 mx-auto">
                      {totalPages > BLOCK_SIZE && (
                        <button
                          className="h-8 px-3 rounded-md border text-sm disabled:opacity-50"
                          disabled={pageState === 1}
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                        >
                          이전
                        </button>
                      )}
                      {Array.from({ length: blockEnd - blockStart + 1 }, (_, i) => blockStart + i).map((n) => (
                        <button
                          key={n}
                          className={`h-8 px-3 rounded-md border text-sm ${n === pageState ? "bg-violet-600 text-white border-violet-600" : ""}`}
                          onClick={() => setPage(n)}
                        >
                          {n}
                        </button>
                      ))}
                      {totalPages > BLOCK_SIZE && (
                        <button
                          className="h-8 px-3 rounded-md border text-sm disabled:opacity-50"
                          disabled={pageState === totalPages}
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        >
                          다음
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </section>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CmsAdmins;
